import { useAuth } from "@/hooks/useAuth";
import {
  trackReportDeleteSuccess,
  trackReportLikeAdd,
  trackReportLikeRemove,
} from "@/lib/analytics";
import { deleteReport } from "@/lib/reports";
import { toggleLike, getUserLike } from "@/lib/votes";
import { SurfReport, WAVE_SIZES, CONDITIONS, CROWD_LEVELS } from "@/types/surfReport";
import EditIcon from "@mui/icons-material/Edit";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PlaceIcon from "@mui/icons-material/Place";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import ReportCardMedia from "./ReportCardMedia";
import VoteButtons from "./VoteButtons";

interface ReportCardProps {
  report: SurfReport;
  onAuthRequired: () => void;
  onEdit: (report: SurfReport) => void;
}

const EDIT_WINDOW_MS = 10 * 60 * 1000;

function getAgeMs(report: SurfReport): number {
  if (!report.createdAt?.toDate) return 0;
  return Date.now() - report.createdAt.toDate().getTime();
}

function getTimeAgo(report: SurfReport): string {
  if (!report.createdAt?.toDate) return "just now";
  return formatDistanceToNow(report.createdAt.toDate(), { addSuffix: true });
}

export default function ReportCard({ report, onAuthRequired, onEdit }: ReportCardProps) {
  const { user } = useAuth();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [liking, setLiking] = useState(false);
  const [localLikeCount, setLocalLikeCount] = useState(report.likeCount ?? 0);

  const conditionConfig = CONDITIONS[report.conditions];
  const crowdConfig = CROWD_LEVELS[report.crowd];
  const hasMedia = !!report.mediaURL && !!report.mediaType;

  const isOwner = user?.uid === report.userId;
  const canEdit = isOwner && getAgeMs(report) < EDIT_WINDOW_MS;

  useEffect(() => {
    if (!user) return;
    getUserLike(report.id, user.uid)
      .then(setLiked)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch user like:", err);
        setLiked(false);
      });
  }, [report.id, user]);

  useEffect(() => {
    setLocalLikeCount(report.likeCount ?? 0);
  }, [report.likeCount]);

  const handleLike = async () => {
    if (!user) {
      onAuthRequired();
      return;
    }
    if (liking) return;
    setLiking(true);
    try {
      const nowLiked = await toggleLike(report.id, user.uid);
      setLiked(nowLiked);
      setLocalLikeCount((prev) => prev + (nowLiked ? 1 : -1));
      if (nowLiked) trackReportLikeAdd();
      else trackReportLikeRemove();
    } finally {
      setLiking(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteReport(report.id);
      trackReportDeleteSuccess();
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
          mb: 0.5,
        }}
      >
        {/* ── Header row ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            py: 1,
          }}
        >
          <Avatar src={report.userPhotoURL || undefined} sx={{ width: 36, height: 36 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: "text.primary",
                }}
              >
                {report.userDisplayName}
              </Typography>
              {report.cameraName && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                  }}
                >
                  <PlaceIcon sx={{ fontSize: 12 }} />
                  {report.cameraName}
                </Typography>
              )}
            </Box>
            {/* Condition summary: serif wave size + dot-labeled conditions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.375,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: "1rem",
                  color: "text.primary",
                  lineHeight: 1,
                }}
              >
                {WAVE_SIZES[report.waveSize].label}
              </Typography>
              <ConditionDot color={conditionConfig.color} label={conditionConfig.label} />
              <ConditionDot color={crowdConfig.color} label={crowdConfig.label} />
            </Box>
          </Box>

          {/* Owner menu */}
          {isOwner && (
            <Box sx={{ display: "flex" }}>
              {canEdit && (
                <IconButton
                  size="small"
                  onClick={() => onEdit(report)}
                  sx={{
                    color: "text.secondary",
                    p: 0.5,
                    "&:hover": { color: "accent.main" },
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => setConfirmDeleteOpen(true)}
                sx={{
                  color: "text.secondary",
                  p: 0.5,
                  "&:hover": { color: "buoy.main" },
                }}
              >
                <MoreHorizIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* ── Full-bleed media ── */}
        {hasMedia && (
          <ReportCardMedia url={report.mediaURL!} type={report.mediaType!} fullWidth />
        )}

        {/* ── Action row (like Instagram: heart, confirm, deny) ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            pt: hasMedia ? 1 : 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={handleLike}
            disabled={liking}
            sx={{
              p: 0.5,
              color: liked ? "buoy.main" : "text.secondary",
              transition: "transform 0.15s ease, color 0.15s ease",
              "&:active": { transform: "scale(0.85)" },
              "&:hover": { color: liked ? "buoy.main" : "text.primary" },
            }}
          >
            {liked ? (
              <FavoriteIcon sx={{ fontSize: 22 }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: 22 }} />
            )}
          </IconButton>

          <VoteButtons
            reportId={report.id}
            confirmCount={report.confirmCount}
            denyCount={report.denyCount}
            onAuthRequired={onAuthRequired}
          />
        </Box>

        {/* ── Like count ── */}
        {localLikeCount > 0 && (
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "text.primary",
              pt: 0.25,
            }}
          >
            {localLikeCount} {localLikeCount === 1 ? "like" : "likes"}
          </Typography>
        )}

        {/* ── Comment (Instagram style: bold name + text) ── */}
        {report.comment && (
          <Box sx={{ pt: 0.5 }}>
            <Typography
              sx={{
                fontSize: "0.88rem",
                lineHeight: 1.45,
                color: "text.primary",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  color: "text.primary",
                }}
              >
                {report.userDisplayName}
              </Typography>{" "}
              {report.comment}
            </Typography>
          </Box>
        )}

        {/* ── Timestamp ── */}
        <Typography
          variant="overline"
          sx={{
            fontSize: "0.62rem",
            color: "text.secondary",
            pt: 0.75,
            display: "block",
          }}
        >
          {getTimeAgo(report)}
        </Typography>
      </Box>

      {/* Delete confirmation */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>Delete report?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
            This will permanently remove your report.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ConditionDot({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.375 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="overline"
        sx={{
          fontSize: "0.62rem",
          color: "text.secondary",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
